import { getEntityId, getNombreCompleto } from "../../layout/paciente/patientHelpers";

// Toda la logica del wizard vive aca como funciones puras (sin estado ni hooks),
// asi el componente solo orquesta.

// Deja cada turno del backend con solo lo que necesita el wizard.
export const mapearTurno = (turno) => ({
    id: getEntityId(turno),
    medicoId: getEntityId(turno.id_medico),
    medicoNombre: getNombreCompleto(turno.id_medico, "Médico"),
    servicios: (turno.servicios || []).map((s) => ({
        id: getEntityId(s),
        nombre: s?.nombre || "Servicio",
        costoConsulta: s?.costoConsulta ?? 0,
        duracionEnMinutos: s?.duracionEnMinutos ?? 30,
    })),
    sedeNombre: turno.sede?.nombre || "Sede",
    fechaHora: turno.fechaHora,
});

// Clave del dia (YYYY-MM-DD en hora local) para agrupar turnos por fecha.
export const claveDia = (fechaHora) => {
    const f = new Date(fechaHora);
    const pad = (n) => String(n).padStart(2, "0");
    return `${f.getFullYear()}-${pad(f.getMonth() + 1)}-${pad(f.getDate())}`;
};

export const formatearFecha = (fechaHora) => {
    const f = new Date(fechaHora);
    if (Number.isNaN(f.getTime())) return "";
    return f.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
};

export const formatearHora = (fechaHora) => {
    const f = new Date(fechaHora);
    if (Number.isNaN(f.getTime())) return "";
    return f.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
};

// Deja solo los turnos con fecha futura (el backend no filtra por fecha).
export const filtrarFuturos = (turnos) => {
    const ahora = new Date();
    return turnos.filter((t) => new Date(t.fechaHora) >= ahora);
};

// Medicos distintos que tienen turnos, filtrados por lo que se escribio.
export const derivarMedicos = (turnos, termino = "") => {
    const medicos = [];
    const vistos = new Set();
    for (const t of turnos) {
        if (t.medicoId && !vistos.has(t.medicoId)) {
            vistos.add(t.medicoId);
            medicos.push({ id: t.medicoId, nombre: t.medicoNombre });
        }
    }
    const q = termino.trim().toLowerCase();
    return medicos.filter((m) => m.nombre.toLowerCase().includes(q));
};

// Servicios distintos que tienen turnos.
export const derivarServicios = (turnos) => {
    const servicios = [];
    const vistos = new Set();
    for (const t of turnos) {
        for (const s of t.servicios) {
            if (s.id && !vistos.has(s.id)) {
                vistos.add(s.id);
                servicios.push(s);
            }
        }
    }
    return servicios;
};

// Turnos que cumplen el criterio del paso 1 (medico elegido o servicio elegido).
export const filtrarPorCriterio = (turnos, tipo, medicoSel, servicioSel) =>
    turnos.filter((t) => {
        if (tipo === "nombre" && medicoSel) return t.medicoId === medicoSel.id;
        if (tipo === "servicio" && servicioSel) return t.servicios.some((s) => s.id === servicioSel.id);
        return false;
    });

// Fechas (dias) distintas de un conjunto de turnos, ordenadas cronologicamente.
export const derivarFechas = (turnos) => {
    const map = new Map();
    for (const t of turnos) {
        const clave = claveDia(t.fechaHora);
        if (!map.has(clave)) {
            map.set(clave, { clave, fechaHora: t.fechaHora, label: formatearFecha(t.fechaHora) });
        }
    }
    return [...map.values()].sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
};

// Turnos de un dia puntual, ordenados por hora.
export const filtrarPorFecha = (turnos, clave) =>
    turnos
        .filter((t) => claveDia(t.fechaHora) === clave)
        .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
